// build: 2026-02-19 cache-bust for production chunk
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, Scale } from "lucide-react";
import FeaturedConversations from "../components/FeaturedConversations";

// ───────────────────────────────────────────────
// THE ETHICAL AI NOW MANIFESTO — verbatim text
// ───────────────────────────────────────────────
const SECTIONS = [
  {
    id: "preamble",
    number: "00",
    kicker: "Preamble",
    title: "Preamble",
    body: [
      "We are entering the most transformative period in human history. Artificial Intelligence is no longer a distant concept confined to science fiction, research laboratories, or Silicon Valley boardrooms. It is rapidly reshaping civilization itself — transforming work, childhood, communication, identity, governance, economics, warfare, creativity, spirituality, and even our understanding of what it means to be human.",
      "The question is no longer whether AI will change the world. The question is: What kind of world will AI create? And more importantly: What kind of humanity will emerge alongside it?",
      "At Ethical AI Now, we believe technological progress without moral wisdom is dangerous. History teaches us that every industrial and technological revolution brings both extraordinary opportunity and profound risk. Today, humanity stands at the threshold of another revolution — not of muscle and machinery, but of cognition itself.",
      "AI systems now shape attention, emotion, memory, truth, behavior, relationships, labor, governance, and childhood development at planetary scale. If left unchecked, the concentration of power risks creating a future where human dignity becomes secondary to efficiency, truth becomes unstable, and humanity itself becomes increasingly disconnected.",
      "We reject the reduction of human beings to data points. Human beings possess dignity.",
    ],
  },
  {
    id: "our-core-belief",
    number: "01",
    kicker: "Our Core Belief",
    title: "Our Core Belief",
    pullquote: "Ethics must lead AI — not chase it. Human values must shape technological development from the beginning.",
    body: [],
  },
  {
    id: "human-dignity",
    number: "02",
    kicker: "Article I",
    title: "Human Dignity Above All",
    body: [
      "At the center of every technological decision must remain the human person. We affirm the dignity of the child, work, truth, conscience, relationships, spiritual inquiry, communities, and moral responsibility.",
    ],
  },
  {
    id: "children-first",
    number: "03",
    kicker: "Article II",
    title: "The Children Must Come First",
    body: [
      "Children cannot become experimental subjects in an unregulated digital environment. We support age-appropriate safeguards and global frameworks that place wellbeing ahead of profit.",
    ],
  },
  {
    id: "cognitive-sovereignty",
    number: "04",
    kicker: "Article III",
    title: "Cognitive Sovereignty",
    body: [
      "Human beings must retain the capacity to think deeply and reason freely without continuous manipulation. Technological convenience cannot come at the cost of human agency.",
    ],
  },
  {
    id: "global-frameworks",
    number: "05",
    kicker: "Article IV",
    title: "Global Ethical Frameworks",
    body: [
      "Humanity requires shared ethical principles capable of transcending religious and ideological divisions. This is why movements like The Call to Rome for AI Ethics matter.",
    ],
  },
  {
    id: "technology-and-the-sacred",
    number: "06",
    kicker: "Article V",
    title: "Technology and the Sacred",
    body: [
      "No machine can replace human conscience, moral responsibility, authentic love, or the sacred value of the person.",
    ],
    coda: "New Machines. Same Soul.",
  },
  {
    id: "environment",
    number: "07",
    kicker: "Article VI",
    title: "Environmental Responsibility",
    body: [
      "Technological advancement must remain compatible with environmental stewardship and intergenerational justice.",
    ],
  },
  {
    id: "work",
    number: "08",
    kicker: "Article VII",
    title: "The Future of Work",
    body: [
      "AI should augment human capability — not reduce human beings to disposable components. We support dignity in labor and fair transitions.",
    ],
  },
  {
    id: "not-anti-technology",
    number: "09",
    kicker: "Position",
    title: "We Are Not Anti-Technology",
    body: [
      "We believe AI holds extraordinary potential to cure disease and solve global challenges, but capability alone does not guarantee wisdom.",
    ],
  },
  {
    id: "call-to-leadership",
    number: "10",
    kicker: "Call to leadership",
    title: "A Call to Leadership",
    body: [
      "We call upon technologists, parents, policymakers, and faith leaders to help shape a future where technology serves humanity. Moral silence is no longer a neutral position.",
    ],
  },
];

const DECLARATION_LINES = [
  "Man before Machine.",
  "Human dignity before profit.",
  "Wisdom before acceleration.",
  "Children before engagement metrics.",
  "Stewardship before exploitation.",
  "Truth before manipulation.",
  "Humanity before ideology.",
];

export default function Manifesto() {
  return (
    <div data-testid="manifesto-page" className="bg-white text-[var(--eai-ink)]">
      {/* CINEMATIC HERO — magazine spread, full image visible, no crop */}
      <section
        className="relative w-full bg-[var(--eai-ink)] text-white border-b border-[var(--eai-ink)]"
        data-testid="manifesto-cinematic-hero"
      >
        {/* Top frame line */}
        <div className="px-6 lg:px-12 py-6 flex items-center justify-between text-white/80">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono-eai uppercase tracking-[0.22em] hover:text-white"
            data-testid="manifesto-back-home"
          >
            <ArrowLeft size={14} /> Ethical AI Now
          </Link>
          <p className="font-mono-eai text-[11px] uppercase tracking-[0.28em]">Manifesto · Edition 01</p>
        </div>

        <div className="px-6 lg:px-12 pb-12 lg:pb-16">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Image column — natural aspect, fully visible */}
            <div className="lg:col-span-7 order-1">
              <figure className="relative w-full">
                <img
                  src="https://customer-assets.emergentagent.com/wingman/8b845ce7-5d58-480c-9d7c-7850e94166e2/attachments/19905f2827a14513a478168ed2eec533_image.bin"
                  alt="Matthew Kilkenny reading Magnifica Humanitas"
                  className="w-full h-auto object-contain max-h-[85vh] mx-auto block"
                  data-testid="manifesto-hero-image"
                />
                <figcaption className="mt-4 flex items-center justify-between text-[11px] font-mono-eai uppercase tracking-[0.22em] text-white/55">
                  <span>Matthew Kilkenny — Founder, Ethical AI Now</span>
                  <span className="flex items-center gap-2"><span className="text-[var(--eai-accent)]">●</span> 2026</span>
                </figcaption>
              </figure>
            </div>

            {/* Title column */}
            <div className="lg:col-span-5 order-2">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.28em] text-[var(--eai-accent)]">
                The Ethical AI Now Manifesto
              </p>
              <h1 className="mt-6 font-display font-bold tracking-tight leading-[0.92] text-6xl sm:text-7xl lg:text-[88px] xl:text-[104px] text-white">
                New Machines.<br />
                <span className="text-[var(--eai-accent)] italic">Same Soul.</span>
              </h1>
              <p className="mt-8 font-display text-xl lg:text-2xl text-white/85 leading-relaxed max-w-xl">
                A Declaration for Human Dignity in the Age of Artificial Intelligence.
              </p>
              <p className="mt-10 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/50">
                Ethical AI Now · Independent · Global
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BODY — sections */}
      <section>
        <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          {SECTIONS.map((s) => (
            <article
              key={s.id}
              id={s.id}
              className="py-16 lg:py-20 border-b border-[var(--eai-border)] first:pt-0 last:border-b-0"
              data-testid={`manifesto-section-${s.id}`}
            >
              <div className="flex items-baseline gap-6 mb-8">
                <span className="font-mono-eai text-sm text-[var(--eai-accent)]">{s.number}</span>
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">{s.kicker}</p>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
                {s.title}
              </h2>

              {s.pullquote && (
                <blockquote className="mt-10 border-l-2 border-[var(--eai-accent)] pl-6 font-display italic text-2xl sm:text-3xl leading-[1.35]">
                  “{s.pullquote}”
                </blockquote>
              )}

              {s.body.length > 0 && (
                <div className="mt-8 space-y-6">
                  {s.body.map((para, i) => (
                    <p key={`${s.id}-p-${i}`} className="font-display text-xl lg:text-[22px] leading-[1.6] text-[var(--eai-ink)]">
                      {para}
                    </p>
                  ))}
                </div>
              )}

              {s.coda && (
                <p className="mt-10 font-display italic text-3xl sm:text-4xl text-[var(--eai-accent)] tracking-tight">
                  {s.coda}
                </p>
              )}
            </article>
          ))}

          {/* DECLARATION — emphasised */}
          <article id="declaration" className="mt-16 lg:mt-20 border-t-2 border-[var(--eai-ink)] pt-16 lg:pt-20" data-testid="manifesto-section-declaration">
            <div className="flex items-baseline gap-6 mb-8">
              <span className="font-mono-eai text-sm text-[var(--eai-accent)]">11</span>
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">Our Declaration</p>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              Our Declaration
            </h2>

            <ul className="mt-12 divide-y divide-[var(--eai-border)] border-y border-[var(--eai-border)]" data-testid="declaration-list">
              {DECLARATION_LINES.map((line, i) => (
                <li
                  key={line}
                  className="py-6 flex items-center gap-6"
                  data-testid={`declaration-line-${i}`}
                >
                  <Scale size={20} strokeWidth={1.6} className="text-[var(--eai-accent)] flex-shrink-0" />
                  <p className="font-display text-2xl sm:text-3xl lg:text-[34px] leading-[1.2] tracking-tight font-medium">
                    {line}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-[var(--eai-border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-display text-2xl font-semibold">Matthew Kilkenny</p>
                <p className="mt-1 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">Founder · Ethical AI Now</p>
              </div>
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">2026</p>
            </div>
          </article>
        </div>
      </section>

      {/* FEATURED CONVERSATIONS — historical record */}
      <FeaturedConversations />

      {/* HOW TO GET INVOLVED */}
      <section className="border-t border-[var(--eai-border)] bg-[var(--eai-ink)] text-white" data-testid="manifesto-get-involved">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
            <div className="lg:col-span-4">
              <p className="overline text-[var(--eai-accent)]">Act now</p>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
                How to Get Involved.
              </h2>
              <p className="mt-6 text-lg lg:text-xl text-white/75 leading-relaxed max-w-2xl">
                Conviction matters. Action matters more. Five practical steps for anyone moved by this manifesto — choose one, or all five.
              </p>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/15" data-testid="get-involved-actions">
            {/* Action 01 — IASEAI */}
            <a
              href="https://www.iaseai.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[var(--eai-ink)] p-8 lg:p-10 flex flex-col hover:bg-black transition-colors duration-200"
              data-testid="involved-action-iaseai"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">01 · Join</p>
                <ArrowUpRight size={22} className="text-white/50 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-8 font-display text-2xl lg:text-3xl font-semibold leading-snug">
                The International Association for Safe and Ethical AI (IASEAI)
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed flex-1">
                Led by President Professor Stuart Russell. The first member-led elections for new association positions are taking place this week — a critical moment to join, vote, and help shape the governance of one of the most consequential bodies in global AI safety.
              </p>
              <p className="mt-8 pt-6 border-t border-white/15 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[var(--eai-accent)] transition-colors">
                iaseai.org →
              </p>
            </a>

            {/* Action 02 — Human Statement on AI */}
            <a
              href="https://humanstatement.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[var(--eai-ink)] p-8 lg:p-10 flex flex-col hover:bg-black transition-colors duration-200"
              data-testid="involved-action-human-statement"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">02 · Sign</p>
                <ArrowUpRight size={22} className="text-white/50 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-8 font-display text-2xl lg:text-3xl font-semibold leading-snug">
                The Human Statement on AI
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed flex-1">
                A public declaration calling for AI development that keeps the human person — dignity, conscience, agency — at the centre of every decision.
              </p>
              <p className="mt-8 pt-6 border-t border-white/15 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[var(--eai-accent)] transition-colors">
                humanstatement.org →
              </p>
            </a>

            {/* Action 03 — Rome Call */}
            <a
              href="https://www.romecall.org"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[var(--eai-ink)] p-8 lg:p-10 flex flex-col hover:bg-black transition-colors duration-200"
              data-testid="involved-action-rome-call"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">03 · Sign</p>
                <ArrowUpRight size={22} className="text-white/50 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-8 font-display text-2xl lg:text-3xl font-semibold leading-snug">
                The Rome Call for AI Ethics
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed flex-1">
                The cross-tradition declaration launched in 2020 at the Pontifical Academy for Life — transparency, inclusion, responsibility, impartiality, reliability, security, and privacy.
              </p>
              <p className="mt-8 pt-6 border-t border-white/15 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[var(--eai-accent)] transition-colors">
                romecall.org →
              </p>
            </a>

            {/* Action 04 — Anxious Generation / Jonathan Haidt */}
            <a
              href="https://www.anxiousgeneration.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[var(--eai-ink)] p-8 lg:p-10 flex flex-col hover:bg-black transition-colors duration-200"
              data-testid="involved-action-techno-skeptics"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">04 · Read</p>
                <ArrowUpRight size={22} className="text-white/50 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-8 font-display text-2xl lg:text-3xl font-semibold leading-snug">
                Join the Techno-Skeptics
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed flex-1">
                Support the mission of <span className="text-white">Jonathan Haidt</span> and read <em className="text-white">The Anxious Generation</em> — the case for protecting the next generation from the Great Rewiring of childhood by smartphones and algorithms.
              </p>
              <p className="mt-8 pt-6 border-t border-white/15 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[var(--eai-accent)] transition-colors">
                anxiousgeneration.com →
              </p>
            </a>

            {/* Action 05 — Ethical AI Now Manifesto (brand action, accent treatment) */}
            <Link
              to="/manifesto"
              className="group bg-[var(--eai-accent)] p-8 lg:p-10 flex flex-col hover:bg-[#B91C0C] transition-colors duration-200"
              data-testid="involved-action-ean-manifesto"
            >
              <div className="flex items-start justify-between">
                <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white">05 · Stand with us</p>
                <ArrowRight size={22} className="text-white" />
              </div>
              <h3 className="mt-8 font-display text-2xl lg:text-3xl font-semibold leading-snug">
                The Ethical AI Now Manifesto
              </h3>
              <p className="mt-4 text-white/85 leading-relaxed flex-1">
                Read, share, and stand with our declaration. <em>New Machines. Same Soul.</em> — a commitment to human dignity in the age of artificial intelligence.
              </p>
              <p className="mt-8 pt-6 border-t border-white/30 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white">
                ethicalainow.com/manifesto →
              </p>
            </Link>
          </div>

          {/* Closing — contact CTA */}
          <div className="mt-20 pt-10 border-t border-white/15 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 text-sm font-medium hover:bg-white hover:text-[var(--eai-ink)] hover:border-white transition-colors duration-200"
              data-testid="manifesto-contact-cta"
            >
              Contact the practice <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER'S POSTSCRIPT — final word, bottom of the manifesto */}
      <section className="border-t border-[var(--eai-border)] bg-white" data-testid="manifesto-postscript">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <p className="overline">Founder&rsquo;s Postscript</p>
          <blockquote className="mt-10 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-[var(--eai-ink)] tracking-tight">
            “Moral silence is no longer a neutral position. I am building this site as a living architecture for the conscience.”
          </blockquote>
          <div className="mt-12 inline-flex flex-col items-center">
            <div className="w-16 h-px bg-[var(--eai-accent)]" />
            <p className="mt-5 font-display text-xl">Matthew Kilkenny</p>
            <p className="mt-1 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">Founder · Ethical AI Now</p>
          </div>
        </div>
      </section>
    </div>
  );
}
